import { ChartBar, CreditCard } from '@phosphor-icons/react';
import * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type UsageIndicatorStatus = 'warning' | 'reached';

export interface UsageIndicatorPillProps {
  /** Current usage as a percentage 0–100 */
  percentage: number;
  /** Name of the usage metric being displayed */
  label?: string;
  /** Visual state of the pill */
  status: UsageIndicatorStatus;
  /** Called when the user selects "View plan" */
  onViewPlan?: () => void;
  /** Called when the user selects "View billing settings" */
  onViewBilling?: () => void;
}

/**
 * Gives an indication of query usage in the platform.
 *
 * A pill button for the top navigation bar that shows the team's monthly usage
 * usage percentage. At ≥80% it renders in warning (yellow) state; at 100% in
 * reached (red) state. Clicking it opens a dropdown with links to the plan and
 * billing settings pages.
 */
export function UsageIndicatorPill({
  percentage,
  label = 'Queries',
  status,
  onViewPlan,
  onViewBilling,
}: UsageIndicatorPillProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center gap-1.5 rounded border bg-surface py-1 pl-2 pr-2.5',
            'text-xs font-normal text-dark-900 cursor-pointer',
            'hover:bg-dark-200 active:bg-dark-400',
            'focus-visible:outline-none',
            'transition-colors',
            {
              'border-yellow-500/50': status === 'warning',
              'border-red-500/50': status === 'reached',
            },
          )}
          aria-label={`${label} usage: ${percentage}%`}
        >
          <span
            className={cn('h-2 w-2 shrink-0 rounded-full', {
              'bg-yellow-500': status === 'warning',
              'bg-red-500': status === 'reached',
            })}
            aria-hidden="true"
          />
          <span className="whitespace-nowrap">
            {percentage}% of {label} used
          </span>
          <span className="opacity-60 whitespace-nowrap">View plan</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onClick={onViewPlan} className="flex items-center gap-2">
          <ChartBar size={16} aria-hidden="true" />
          View plan
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onViewBilling} className="flex items-center gap-2">
          <CreditCard size={16} aria-hidden="true" />
          View billing settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
